import React from 'react';
import PropTypes from 'prop-types';
import { Card } from 'react-bootstrap';
const uuid = require('uuid');
const sortBy = require('lodash/sortBy');
const get = require('lodash/get');

const Globals = require('../../../../app/globals');
const Strings = require('../../../../app/shared/Strings');
const StringUtil = require('../../../../app/shared/StringUtil');
const PermissionsUtil = require('../../../../app/shared/PermissionsUtil');
const ProjectStore = require('../../stores/ProjectStore');
const CollaboratorStore = require('../../stores/CollaboratorStore');
import ProjectRoleDropdown from '../common/ProjectRoleDropdown';
import AccessibleIcon from '../common/AccessibleIcon';
import ConfirmModal from '../modal/ConfirmModal';
const CollaboratorActions = require('../../actions/CollaboratorActions');
import ProjectDropdown from '../project/ProjectDropdown';
import InfoIcon from '../common/InfoIcon';

const propTypes = {
    user: PropTypes.object.isRequired,
    orgId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    ownUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    ownUserPermissions: PropTypes.object.isRequired,
};

const defaultProps = {};

class OrgMemberProjectPermissions extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            projectStoreState: ProjectStore.getStateChannel(props.orgId),
            collaboratorStoreState: CollaboratorStore.getState(),
            removeUserFxn: null,
            removeUserMsg: '',
            newProjectId: 0,
            newProjectRole: '',
            addActionId: null,
            modalState: {
                "confirmRemove": false,
            }
        };

        this.onProjectRoleChange = this.onProjectRoleChange.bind(this);
        this.onRemoveUser = this.onRemoveUser.bind(this);
        this.removeUser = this.removeUser.bind(this);
        this.closeConfirmModal = this.closeConfirmModal.bind(this);
        this.onNewProjectSelect = this.onNewProjectSelect.bind(this);
        this.onNewProjectRoleSelect = this.onNewProjectRoleSelect.bind(this);
        this.onAddNewProjectRole = this.onAddNewProjectRole.bind(this);

        this.unsubscribes = [
            ProjectStore.listen(this.onProjectStoreChange.bind(this)),
            CollaboratorStore.listen(this.onCollaboratorStoreChange.bind(this))
        ];
    }

    componentDidMount() {
        CollaboratorActions.getChannelProjectsCollabs(uuid.v4(), { channelId: this.props.orgId });
    }

    componentWillUnmount() {
        this.unsubscribes.forEach(unsubscribeCallback => { unsubscribeCallback(); });
    }

    onProjectStoreChange(state) {
        this.setState({ projectStoreState: ProjectStore.getStateChannel(this.props.orgId) });
    }

    onCollaboratorStoreChange(state) {
        this.setState({ collaboratorStoreState: state });
    }

    onProjectRoleChange(projectId, roleToken) {
        CollaboratorActions.setProjectPermission(uuid.v4(), {
            projectID: projectId,
            userId: this.props.user.id,
            roleToken: roleToken
        });
    }

    onRemoveUser(projectId, projectName) {
        this.setState({
            removeUserMsg: `Are you sure you would like to remove ${this.props.user.first_name} ${this.props.user.last_name} from ${projectName}?`,
            removeUserFxn: this.removeUser.bind(this, projectId),
            modalState: { ...this.state.modalState, confirmRemove: true }
        });
    }

    removeUser(projectId) {
        this.setState({ modalState: { ...this.state.modalState, confirmRemove: false } });
        CollaboratorActions.deleteProjectPermission(uuid.v4(), {
            projectID: projectId,
            userId: this.props.user.id
        });
    }

    closeConfirmModal() {
        this.setState({
            removeUserMsg: '',
            removeUserFxn: null,
            modalState: { ...this.state.modalState, confirmRemove: false }
        });
    }

    onNewProjectSelect(projectId) {
        this.setState({ newProjectId: parseInt(projectId, 10) });
    }

    onNewProjectRoleSelect(projectRole) {
        this.setState({ newProjectRole: projectRole });
    }

    onAddNewProjectRole() {
        if (!this.state.newProjectId || !this.state.newProjectRole) {
            return;
        }

        CollaboratorActions.setProjectPermission(uuid.v4(), {
            projectID: this.state.newProjectId,
            email: this.props.user.email,
            roleToken: this.state.newProjectRole
        });
        this.setState({ newProjectId: 0, newProjectRole: '' });
    }

    render() {
        const self = this;
        const hasOrgLevelEditCollabs = PermissionsUtil.canEditProjectCollaborators(this.props.ownUserPermissions);
        const ownCollabRecords = {};
        const excludedFromNewProjectList = [];
        let sortedProjects = [];
        if (this.state.projectStoreState.initialized) {
            sortedProjects = sortBy(this.state.projectStoreState.projects, p => { return p.name.toLowerCase(); });
        }

        sortedProjects.forEach((project) => { // map own project permissions for non-org-level users
            const projCollabs = this.state.collaboratorStoreState.pCollaborators[project.id];
            if (projCollabs && projCollabs.initialized) {
                ownCollabRecords[project.id] = get(
                    projCollabs.pCollaborators.find(collab => { return collab.id == this.props.ownUserId }),
                    ['project_permissions'],
                    {}
                );
            }
        });
        
        const canEditForNewProject = !!this.state.newProjectId && PermissionsUtil.canEditProjectCollaborators(this.props.ownUserPermissions, ownCollabRecords[this.state.newProjectId]);
        return (
            <Card body bg="light" className="org-member-project-roles">
                { [Globals.users.roles.ORG_OWNER, Globals.users.roles.ORG_ADMIN].indexOf(this.props.user.role_token) >= 0 &&
                    <div className="font-size-14px">
                        <InfoIcon className="margin-right-sm" />
                        Organization {StringUtil.getStringForRoleToken(Globals.users.roles.ORG_OWNER, true)} and {StringUtil.getStringForRoleToken(Globals.users.roles.ORG_ADMIN, true)} do not require specific project roles.
                        These users automatically have {StringUtil.getStringForRoleToken(Globals.users.roles.PROJECT_MANAGER, false)} permissions for all organization projects.
                    </div>
                }
                <div className="termListing--noBorder">
                    <h2 className="termListing__termContainer font-weight-bold">
                        {Strings['en'].project.collaborators.PROJECT_COLLAB_ROLES}
                    </h2>
                </div>
                <ul>
                    <li key="title" className="termListing">
                        <div className="termListing__termContainer font-weight-bold">Project</div>
                        <div className="termListing__control font-weight-bold">Role</div>
                    </li>
                {   sortedProjects.map(project => { // build list of elements for projects user has collab permissions in. also create list of exclusions for list of projects where current user can add permissions
                        let userCollabRecord;
                        const projCollabs = this.state.collaboratorStoreState.pCollaborators[project.id];
                        const canEditProjectCollaborators = PermissionsUtil.canEditProjectCollaborators(this.props.ownUserPermissions, ownCollabRecords[project.id]);
                        if (projCollabs && projCollabs.initialized) {
                            userCollabRecord = projCollabs.pCollaborators.find(collab => { return collab.id === this.props.user.id });
                            if (!userCollabRecord) {
                                if (!canEditProjectCollaborators) {
                                    excludedFromNewProjectList.push(project.id);    
                                }

                                return null;
                            }
                            else { // a collab record does exist for this person in this project
                                excludedFromNewProjectList.push(project.id);
                            }
                        }
                        else { // a list of collaborators does not exist for this project, allow adding if permitted for the user viewing this component
                            if (!canEditProjectCollaborators) {
                                excludedFromNewProjectList.push(project.id);
                            }

                            return null;
                        }

                        
                        return (
                            <li key={project.id} className="termListing">
                                <div className="termListing__termContainer">{project.name}</div>
                                <div className="termListing__control">
                                    {(canEditProjectCollaborators) ?
                                        <ProjectRoleDropdown
                                            id={`project-roles-${project.id}`}
                                            selectedRoleToken={userCollabRecord.project_role_token}
                                            onRoleChange={self.onProjectRoleChange.bind(self, project.id)}
                                        />
                                        :
                                        StringUtil.getStringForRoleToken(userCollabRecord.project_role_token)
                                    }
                                    {(canEditProjectCollaborators) &&
                                        <button
                                            className="bare-text-button glyph margin-left-md"
                                            onClick={self.onRemoveUser.bind(self, project.id, project.name)}
                                            title={Strings['en'].project.collaborators.REMOVE_USER}
                                        >
                                            <AccessibleIcon label={Strings['en'].project.collaborators.REMOVE_USER}>
                                                <span className="fa fa-remove" aria-hidden={true}/>
                                            </AccessibleIcon>
                                        </button>                                    
                                    }
                                </div>
                            </li>
                        );
                    })
                }
                <li key="newProjectRole" className="termListing--noBorder">
                    <div className="termListing__termContainer">
                        <ProjectDropdown
                            channelId={this.props.orgId}
                            onProjectChange={this.onNewProjectSelect}
                            selectedProjectId={this.state.newProjectId}
                            rejectProjectIds={excludedFromNewProjectList}
                        />
                    </div>
                    <div className="termListing__control">
                        {(canEditForNewProject) &&
                            <ProjectRoleDropdown
                                id="project-roles-new"
                                selectedRoleToken={this.state.newProjectRole}
                                onRoleChange={this.onNewProjectRoleSelect}
                            />
                        }
                        {(canEditForNewProject) &&
                            <button
                                className="bare-text-button glyph margin-left-md"
                                disabled={!!this.state.addActionId || !this.state.newProjectRole || !this.state.newProjectId}
                                onClick={this.onAddNewProjectRole}
                            >
                                <AccessibleIcon label={Strings['en'].project.collaborators.ADD_NEW}>
                                    <span className="fa fa-plus" aria-hidden={true}/>
                                </AccessibleIcon>
                            </button>
                        }

                    </div>
                </li>
                </ul>
                { this.state.modalState.confirmRemove &&
                    <ConfirmModal
                        show={true}
                        modalTitle={Strings['en'].project.collaborators.remove_modal.TITLE}
                        text={this.state.removeUserMsg}
                        buttonText={Strings['en'].common.CONFIRM}
                        closeHandler={this.closeConfirmModal}
                        confirmHandler={this.state.removeUserFxn}
                    />
                }
            </Card>
        );
    }
}

OrgMemberProjectPermissions.propTypes = propTypes;
OrgMemberProjectPermissions.defaultProps = defaultProps;

export default OrgMemberProjectPermissions;
